import {
  createMarketplaceUser,
  getMarketplaceUsers,
  sendInviteEmail,
  updateMarketplaceUser,
} from 'APIs/marketplace.users/marketplace.users';
import { MarketplaceUser, SendMarketplaceUserInvitationRequest } from 'APIs/marketplace.users/marketplace.users.types';
import { Content, LpBox } from 'Constants/styles';
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import getApiErrorMessage from 'utils/getApiErrorMsg';

import UserList from './UserList';
import { UserFormProps, toCreateMarketplaceUserRequest, toUpdateMarketplaceUserRequest } from './types';

const MarketplaceUsers = () => {
  const { marketplaceId } = useParams<{ marketplaceId: string; manage: string }>();
  const { apiBaseUri } = useSelector((state: ReduxStateType) => ({
    apiBaseUri: state.apiBaseUri,
  }));

  const [actionUsers, setActionUsers] = useState<Array<string>>([]);
  const [users, setUsers] = useState<Array<MarketplaceUser>>([]);
  const [fetchingError, setFetchingError] = useState<string>('');
  const [marketplaceUser, setMarketplaceUser] = useState<MarketplaceUser | null>(null);

  const [showModal, setShowModal] = useState<boolean>(false);
  const [isModalLoading, setIsModalLoading] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [modalErrorMsg, setModalErrorMsg] = useState<string>('');

  const getUser = useCallback(
    (userId: string): MarketplaceUser | null => {
      if (users.length === 0) return null;
      const userToEdit = users.filter((user) => user._id === userId);

      if (userToEdit.length === 0) return null;
      return userToEdit[0];
    },
    [users],
  );

  const fetchUsers = useCallback(async () => {
    if (!apiBaseUri) {
      return;
    }

    try {
      setIsLoading(true);
      const resp = await getMarketplaceUsers(apiBaseUri, marketplaceId);
      setUsers(resp);
    } catch (e) {
      setUsers([]);
      const errMsg = getApiErrorMessage(e);
      toast.error(`Error: ${errMsg}`);
      setFetchingError(errMsg);
    } finally {
      setIsLoading(false);
    }
  }, [apiBaseUri, marketplaceId]);

  const resendEmail = useCallback(
    async (userId: string) => {
      if (!apiBaseUri) {
        return;
      }

      const mUser = getUser(userId);
      if (mUser) {
        const confimed = window.confirm(
          `Are you sure you want to resend the onboarding email to ${mUser.emailAddress}?`,
        );
        if (!confimed) return;

        setActionUsers((prev) => [...prev, userId]);
        const payload: SendMarketplaceUserInvitationRequest = {
          emailAddress: mUser.emailAddress,
        };

        try {
          await sendInviteEmail(apiBaseUri, marketplaceId, payload);
          toast.success('Resent the onboarding email successfully');
        } catch (error) {
          toast.error(`Error: ${getApiErrorMessage(error)}`);
        } finally {
          setActionUsers((prev) => prev.filter((uId) => uId !== userId));
        }
      }
    },
    [apiBaseUri, getUser, marketplaceId],
  );

  const saveMarketplaceUserHandler = useCallback(
    async (formData: UserFormProps) => {
      if (!apiBaseUri) {
        return;
      }

      try {
        setIsModalLoading(true);
        setModalErrorMsg('');
        if (formData.userId) {
          const updateUser = toUpdateMarketplaceUserRequest(formData);
          await updateMarketplaceUser(apiBaseUri, marketplaceId, formData.userId, updateUser);
          toast.success('User updated successfully');
        } else {
          const createUser = toCreateMarketplaceUserRequest(formData);
          await createMarketplaceUser(apiBaseUri, marketplaceId, createUser);
          toast.success('User created successfully');
        }
        setShowModal(false);
        fetchUsers();
      } catch (error) {
        setModalErrorMsg(getApiErrorMessage(error));
      } finally {
        setIsModalLoading(false);
      }
    },
    [apiBaseUri, fetchUsers, marketplaceId],
  );

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  return (
    <>
      <Content className="marketplace-invoices-content">
        <LpBox>
          <UserList
            actionUsers={actionUsers}
            users={users}
            fetchingError={fetchingError}
            marketplaceUser={marketplaceUser}
            showModal={showModal}
            setShowModal={setShowModal}
            isModalLoading={isModalLoading}
            isLoading={isLoading}
            modalErrorMsg={modalErrorMsg}
            setModalErrorMsg={setModalErrorMsg}
            setMarketplaceUser={setMarketplaceUser}
            saveMarketplaceUserHandler={saveMarketplaceUserHandler}
            resendEmail={resendEmail}
          />
        </LpBox>
      </Content>
    </>
  );
};

export default MarketplaceUsers;
